import { networkApi } from '../infrastructure/api/NetworkApiClient';

/**
 * An applicable change that can be restored
 */
export interface ApplicableChange {
  id: string;
  elementId: string;
  elementType: string;
  elementName?: string;
  changeType: string;
  changedProperty?: string;
  description: string;
}

/**
 * A conflicting change that cannot be applied
 */
export interface ConflictingChange {
  id: string;
  elementId: string;
  elementType: string;
  elementName?: string;
  changeType: string;
  conflictReason: string;
}

/**
 * Result from the import endpoint containing pending changes
 */
export interface PendingChangesResult {
  applicable: ApplicableChange[];
  conflicts: ConflictingChange[];
}

/**
 * Result of applying changes
 */
export interface ApplyChangesResult {
  successCount: number;
  failedCount: number;
  applied: Array<{
    changeId: string;
    elementId: string;
    changeType: string;
  }>;
  failed: Array<{
    changeId: string;
    elementId: string;
    reason: string;
  }>;
}

/**
 * Service for managing change tracking and recovery operations
 */
export const changeTrackingService = {
  /**
   * Applies selected changes to the current network
   */
  async applyChanges(changeIds: string[]): Promise<ApplyChangesResult> {
    try {
      return await networkApi.zonePost<ApplyChangesResult>('/network/changes/apply', {
        changeIds
      });
    } catch (error) {
      console.error('[ChangeTracking] Failed to apply changes:', error);
      throw error;
    }
  },

  /**
   * Discards selected changes (marks them as not to be applied)
   */
  async discardChanges(changeIds: string[]): Promise<{ discarded: number }> {
    try {
      return await networkApi.zonePost<{ discarded: number }>('/network/changes/discard', {
        changeIds
      });
    } catch (error) {
      console.error('[ChangeTracking] Failed to discard changes:', error);
      throw error;
    }
  },

  /**
   * Gets a human-readable German name for the element type
   */
  getElementTypeDisplayName(elementType: string): string {
    const typeMap: Record<string, string> = {
      trafostation: 'Trafostation',
      kabelverteilschrank: 'Kabelverteilschrank',
      hausanschluss: 'Hausanschluss',
      muffe: 'Muffe',
      abgangsleitung: 'Kabel'
    };
    return typeMap[elementType.toLowerCase()] || elementType;
  },

  /**
   * Gets a human-readable German name for the change type
   */
  getChangeTypeDisplayName(changeType: string): string {
    const typeMap: Record<string, string> = {
      CREATE: 'Hinzugefügt',
      UPDATE: 'Geändert',
      DELETE: 'Gelöscht'
    };
    return typeMap[changeType] || changeType;
  },

  /**
   * Gets a CSS class for the change type badge
   */
  getChangeTypeBadgeClass(changeType: string): string {
    const classMap: Record<string, string> = {
      CREATE: 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200',
      UPDATE: 'bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-200',
      DELETE: 'bg-error-100 text-error-800 dark:bg-error-900 dark:text-error-200'
    };
    return classMap[changeType] || 'bg-primary-100 text-primary-800';
  }
};

export default changeTrackingService;
